import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  runTransaction,
  query,
  orderBy,
  where,
  serverTimestamp,
  writeBatch,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import type { Task, TaskLink, TaskLabel, TaskStatus, TaskType, Priority, TaskChecklistItem } from '@/types'
import type { TasksState } from './state'

export interface CreateTaskData {
  title: string
  description?: string
  type: TaskType
  priority: Priority
  status?: TaskStatus
  boardId?: string
  listId?: string
  assignedTo?: string[]
  projectId?: string
  parentTaskId?: string
  dueDate?: Date
  labels?: string[]
  labelIds?: string[]
  estimatedHours?: number
  checklist?: { id: string; text: string }[]
  links?: TaskLink[]
}

export interface UpdateTaskData {
  title?: string
  description?: string
  type?: TaskType
  priority?: Priority
  status?: TaskStatus
  boardId?: string
  listId?: string
  assignedTo?: string[]
  projectId?: string
  dueDate?: Date | null
  labels?: string[]
  labelIds?: string[]
  estimatedHours?: number
  checklist?: TaskChecklistItem[]
  links?: TaskLink[]
  progress?: number
  archived?: boolean
  boardOrder?: number
  startedAt?: Date | null
  completedAt?: Date | null
}

const mapTaskData = (docSnap: { id: string; data: () => Record<string, unknown> }): Task => {
  const data = docSnap.data()
  return {
    id: docSnap.id,
    tenantId: data.tenantId as string | undefined,
    projectId: data.projectId as string | undefined,
    parentTaskId: data.parentTaskId as string | undefined,
    boardId: data.boardId as string | undefined,
    listId: data.listId as string | undefined,
    taskNumber: data.taskNumber as number | undefined,
    title: (data.title as string) || '',
    description: (data.description as string) || '',
    type: (data.type as TaskType) || 'chore',
    priority: (data.priority as Priority) || 'medium',
    status: (data.status as TaskStatus) || 'backlog',
    assignedTo: (data.assignedTo as string[]) || [],
    reporterId: (data.reporterId as string) || (data.createdBy as string) || '',
    checklist: ((data.checklist as TaskChecklistItem[]) || []).map((item) => ({
      ...item,
      completedAt: (item.completedAt as unknown as { toDate?: () => Date })?.toDate?.() || item.completedAt,
    })),
    links: ((data.links as TaskLink[]) || []),
    progress: (data.progress as number) || 0,
    labels: (data.labels as string[]) || [],
    labelIds: (data.labelIds as string[]) || [],
    dueDate: (data.dueDate as { toDate?: () => Date })?.toDate?.() || undefined,
    startedAt: (data.startedAt as { toDate?: () => Date })?.toDate?.() || undefined,
    completedAt: (data.completedAt as { toDate?: () => Date })?.toDate?.() || undefined,
    estimatedHours: data.estimatedHours as number | undefined,
    archived: (data.archived as boolean) || false,
    boardOrder: (data.boardOrder as number) || 0,
    createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    updatedAt: (data.updatedAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    createdBy: (data.createdBy as string) || '',
    updatedBy: (data.updatedBy as string) || '',
  }
}

export const createActions = (state: TasksState) => {
  const { logCreate, logUpdate, logDelete } = useActivityLog()
  let tasksUnsubscribe: Unsubscribe | null = null
  let globalUnsubscribe: Unsubscribe | null = null
  let isGlobalActive = false

  // All tasks from global subscription (unfiltered by board)
  let globalTasks: Task[] = []

  /**
   * Global subscription: all tasks for the tenant, filtered client-side
   * by board membership (user must be member/creator of the board, or
   * the task must be assigned to the user). Superadmin sees all.
   *
   * Called once on login, lives for the entire session.
   * Board views call subscribeTasks() which temporarily overrides state.tasks.
   * When unsubscribeTasks() is called, state.tasks restores from globalTasks.
   */
  const subscribeGlobal = (boardIds?: string[]): void => {
    if (globalUnsubscribe) return // already active

    const authStore = useAuthStore()
    const userId = authStore.user?.id
    const role = authStore.user?.role

    const tenantConstraint = getTenantConstraint()
    const constraints = tenantConstraint ? [tenantConstraint] : []
    constraints.push(orderBy('createdAt', 'desc'))

    const q = query(collection(db, 'tasks'), ...constraints)

    state.isLoading = true
    isGlobalActive = true

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      let allTasks = snapshot.docs.map(mapTaskData)

      // Client-side visibility filter (mirrors board membership rules)
      if (role !== 'superadmin' && userId) {
        const allowedBoardIds = new Set(boardIds || [])
        allTasks = allTasks.filter((t) =>
          // Task is assigned to user
          t.assignedTo?.includes(userId) ||
          // Task is on a board the user has access to
          (t.boardId && allowedBoardIds.has(t.boardId)) ||
          // Task was created by user
          t.createdBy === userId
        )
      }

      globalTasks = allTasks

      // Only update state.tasks if no board-specific subscription is active
      if (!tasksUnsubscribe) {
        state.tasks = globalTasks
      }
      state.isLoading = false
    }, (error) => {
      console.error('Global tasks subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
      isGlobalActive = false
    }
  }

  /**
   * Board/project-specific subscription. Temporarily overrides global data
   * for the tasks view. When unsubscribed, global data restores automatically.
   */
  const subscribeTasks = (options?: { boardId?: string; projectId?: string }): void => {
    // Cleanup previous board listener
    if (tasksUnsubscribe) {
      tasksUnsubscribe()
      tasksUnsubscribe = null
    }

    state.isLoading = true
    state.error = null

    const tenantConstraint = getTenantConstraint()
    const constraints = tenantConstraint ? [tenantConstraint] : []

    if (options?.boardId) {
      constraints.push(where('boardId', '==', options.boardId))
    }
    if (options?.projectId) {
      constraints.push(where('projectId', '==', options.projectId))
    }

    constraints.push(orderBy('createdAt', 'desc'))

    const q = query(collection(db, 'tasks'), ...constraints)

    tasksUnsubscribe = onSnapshot(q, (snapshot) => {
      state.tasks = snapshot.docs.map(mapTaskData)
      state.isLoading = false
    }, (error) => {
      console.error('Tasks subscription error:', error)
      state.error = 'Error al cargar tareas'
      state.isLoading = false
    })
  }

  const unsubscribeTasks = (): void => {
    if (tasksUnsubscribe) {
      tasksUnsubscribe()
      tasksUnsubscribe = null
    }
    // Restore global data when leaving a board view
    if (isGlobalActive) {
      state.tasks = globalTasks
    }
  }

  const fetchTasks = async (options?: { boardId?: string; projectId?: string }): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const tenantConstraint = getTenantConstraint()
      const constraints = tenantConstraint ? [tenantConstraint] : []

      if (options?.boardId) {
        constraints.push(where('boardId', '==', options.boardId))
      }
      if (options?.projectId) {
        constraints.push(where('projectId', '==', options.projectId))
      }

      constraints.push(orderBy('createdAt', 'desc'))

      const q = query(collection(db, 'tasks'), ...constraints)
      const snapshot = await getDocs(q)
      state.tasks = snapshot.docs.map(mapTaskData)
    } catch (error) {
      console.error('Error fetching tasks:', error)
      state.error = 'Error al cargar tareas'
    } finally {
      state.isLoading = false
    }
  }

  const fetchTaskById = async (id: string): Promise<Task | null> => {
    try {
      const docRef = doc(db, 'tasks', id)
      const docSnap = await getDoc(docRef)
      if (!docSnap.exists()) return null
      const task = mapTaskData({ id: docSnap.id, data: () => docSnap.data() as Record<string, unknown> })
      state.selectedTask = task
      return task
    } catch (error) {
      console.error('Error fetching task:', error)
      return null
    }
  }

  const getNextTaskNumber = async (): Promise<number> => {
    const counterRef = doc(db, 'counters', 'tasks')
    const nextNumber = await runTransaction(db, async (transaction) => {
      const counterSnap = await transaction.get(counterRef)
      const current = counterSnap.exists() ? (counterSnap.data().lastTaskNumber as number) || 0 : 0
      const next = current + 1
      transaction.set(counterRef, {
        lastTaskNumber: next,
        updatedAt: new Date().toISOString(),
      }, { merge: true })
      return next
    })
    return nextNumber
  }

  const createTask = async (data: CreateTaskData): Promise<string> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const taskNumber = await getNextTaskNumber()

    const docRef = await addDoc(collection(db, 'tasks'), {
      ...getTenantData(),
      title: data.title,
      description: data.description || '',
      type: data.type,
      priority: data.priority,
      status: data.status || 'backlog',
      taskNumber,
      boardId: data.boardId || null,
      listId: data.listId || null,
      assignedTo: data.assignedTo || [],
      reporterId: userId,
      projectId: data.projectId || null,
      parentTaskId: data.parentTaskId || null,
      checklist: (data.checklist || []).map((i) => ({ ...i, completed: false })),
      links: (data.links || []).map((l) => ({ id: l.id, url: l.url, ...(l.title ? { title: l.title } : {}) })),
      progress: 0,
      labels: data.labels || [],
      labelIds: data.labelIds || [],
      dueDate: data.dueDate || null,
      estimatedHours: data.estimatedHours || null,
      archived: false,
      boardOrder: Date.now(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId,
      updatedBy: userId,
    })

    await logCreate('task', docRef.id, data.title)
    return docRef.id
  }

  const updateTask = async (id: string, data: UpdateTaskData): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'tasks', id)

    const updateData: Record<string, unknown> = {
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    if (data.title !== undefined) updateData.title = data.title
    if (data.description !== undefined) updateData.description = data.description
    if (data.type !== undefined) updateData.type = data.type
    if (data.priority !== undefined) updateData.priority = data.priority
    if (data.boardId !== undefined) updateData.boardId = data.boardId
    if (data.listId !== undefined) updateData.listId = data.listId
    if (data.status !== undefined) {
      updateData.status = data.status
      if (data.status === 'in_progress' && !data.startedAt) {
        updateData.startedAt = serverTimestamp()
      }
      if (data.status === 'done') {
        updateData.completedAt = serverTimestamp()
      }
    }
    if (data.assignedTo !== undefined) updateData.assignedTo = data.assignedTo || []
    if (data.projectId !== undefined) updateData.projectId = data.projectId || null
    if (data.dueDate !== undefined) updateData.dueDate = data.dueDate
    if (data.labels !== undefined) updateData.labels = data.labels
    if (data.labelIds !== undefined) updateData.labelIds = data.labelIds
    if (data.estimatedHours !== undefined) updateData.estimatedHours = data.estimatedHours
    if (data.checklist !== undefined) {
      updateData.checklist = data.checklist.map((item) => ({
        id: item.id,
        text: item.text,
        completed: item.completed,
        completedAt: item.completedAt ?? null,
        completedBy: item.completedBy ?? null,
      }))
    }
    if (data.links !== undefined) updateData.links = data.links.map((l) => ({
      id: l.id,
      url: l.url,
      ...(l.title ? { title: l.title } : {}),
    }))
    if (data.progress !== undefined) updateData.progress = data.progress
    if (data.archived !== undefined) updateData.archived = data.archived
    if (data.boardOrder !== undefined) updateData.boardOrder = data.boardOrder
    if (data.startedAt !== undefined) updateData.startedAt = data.startedAt
    if (data.completedAt !== undefined) updateData.completedAt = data.completedAt

    await updateDoc(docRef, updateData)

    // Update local state (deep copy checklist for reactivity)
    const mergeData = {
      ...data,
      ...(data.checklist ? { checklist: [...data.checklist] } : {}),
      updatedBy: userId,
      updatedAt: new Date(),
    }
    const idx = state.tasks.findIndex((t) => t.id === id)
    if (idx !== -1) {
      state.tasks[idx] = { ...state.tasks[idx], ...mergeData } as Task
    }
    if (state.selectedTask?.id === id) {
      state.selectedTask = { ...state.selectedTask, ...mergeData } as Task
    }

    const existing = state.tasks.find((t) => t.id === id)
    if (existing) {
      await logUpdate('task', id, [], existing.title)
    }
  }

  const updateTaskStatus = async (id: string, status: TaskStatus): Promise<void> => {
    await updateTask(id, { status })
  }

  const deleteTask = async (id: string): Promise<void> => {
    const existing = state.tasks.find((t) => t.id === id)
    await deleteDoc(doc(db, 'tasks', id))
    state.tasks = state.tasks.filter((t) => t.id !== id)
    if (existing) {
      await logDelete('task', id, existing.title)
    }
  }

  const toggleChecklistItem = async (taskId: string, itemId: string): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const task = state.tasks.find((t) => t.id === taskId) || state.selectedTask
    if (!task) return

    const checklist = task.checklist.map((item) => {
      if (item.id !== itemId) return item
      return {
        ...item,
        completed: !item.completed,
        completedAt: !item.completed ? new Date() : null,
        completedBy: !item.completed ? userId : null,
      }
    })

    const completed = checklist.filter((i) => i.completed).length
    const progress = checklist.length > 0 ? Math.round((completed / checklist.length) * 100) : 0

    await updateTask(taskId, { checklist, progress })
  }

  const addChecklistItem = async (taskId: string, text: string): Promise<void> => {
    const task = state.tasks.find((t) => t.id === taskId) || state.selectedTask
    if (!task) return

    const newItem: TaskChecklistItem = {
      id: crypto.randomUUID(),
      text,
      completed: false,
    }

    const checklist = [...task.checklist, newItem]
    const completed = checklist.filter((i) => i.completed).length
    const progress = checklist.length > 0 ? Math.round((completed / checklist.length) * 100) : 0

    await updateTask(taskId, { checklist, progress })
  }

  const removeChecklistItem = async (taskId: string, itemId: string): Promise<void> => {
    const task = state.tasks.find((t) => t.id === taskId) || state.selectedTask
    if (!task) return

    const checklist = task.checklist.filter((i) => i.id !== itemId)
    const completed = checklist.filter((i) => i.completed).length
    const progress = checklist.length > 0 ? Math.round((completed / checklist.length) * 100) : 0

    await updateTask(taskId, { checklist, progress })
  }

  const updateBoardOrder = async (taskId: string, boardOrder: number): Promise<void> => {
    const docRef = doc(db, 'tasks', taskId)
    await updateDoc(docRef, { boardOrder })
    const idx = state.tasks.findIndex((t) => t.id === taskId)
    if (idx !== -1) {
      state.tasks[idx] = { ...state.tasks[idx], boardOrder }
    }
  }

  const archiveTask = async (taskId: string): Promise<void> => {
    await updateTask(taskId, { archived: true })
  }

  const unarchiveTask = async (taskId: string): Promise<void> => {
    await updateTask(taskId, { archived: false })
  }

  const updateTaskList = async (taskId: string, listId: string): Promise<void> => {
    await updateTask(taskId, { listId })
  }

  // Reordena (y opcionalmente mueve de lista) tarjetas persistiendo boardOrder DENSO (0..N-1)
  // sobre la lista destino, en un solo writeBatch. `orderedIds` es el orden final COMPLETO de la
  // lista destino (incluida la tarjeta movida). A cada tarjeta se le fija listId destino, así el
  // mismo método cubre reorden intra-lista y movimiento entre listas. Espejo del kanban de sprints.
  const reorderTasksInList = async (orderedIds: string[], listId: string): Promise<void> => {
    if (orderedIds.length === 0) return
    const authStore = useAuthStore()
    const userId = authStore.user?.id || 'system'

    const batch = writeBatch(db)
    orderedIds.forEach((id, index) => {
      batch.update(doc(db, 'tasks', id), {
        boardOrder: index,
        listId,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })
    })
    await batch.commit()

    // Update optimista del estado local (onSnapshot igual refresca).
    orderedIds.forEach((id, index) => {
      const t = state.tasks.find((x) => x.id === id)
      if (t) {
        t.boardOrder = index
        t.listId = listId
      }
    })
  }

  // ==========================================
  // Task Labels CRUD
  // ==========================================

  const fetchTaskLabels = async (): Promise<void> => {
    const authStore = useAuthStore()
    const tenantId = authStore.effectiveTenantId
    if (!tenantId) return

    try {
      const docRef = doc(db, 'tenants', tenantId, 'settings', 'taskLabels')
      const snap = await getDoc(docRef)
      if (snap.exists()) {
        state.taskLabels = (snap.data().labels as TaskLabel[]) || []
      } else {
        state.taskLabels = []
      }
    } catch (error) {
      console.error('Error fetching task labels:', error)
    }
  }

  const saveTaskLabels = async (): Promise<void> => {
    const authStore = useAuthStore()
    const tenantId = authStore.effectiveTenantId
    if (!tenantId) return

    const docRef = doc(db, 'tenants', tenantId, 'settings', 'taskLabels')
    const { setDoc } = await import('firebase/firestore')
    await setDoc(docRef, { labels: state.taskLabels }, { merge: true })
  }

  const createTaskLabel = async (name: string, color: string): Promise<TaskLabel> => {
    const newLabel: TaskLabel = {
      id: crypto.randomUUID(),
      name,
      color,
    }
    state.taskLabels.push(newLabel)
    await saveTaskLabels()
    return newLabel
  }

  const updateTaskLabel = async (labelId: string, name: string, color: string): Promise<void> => {
    const idx = state.taskLabels.findIndex((l) => l.id === labelId)
    if (idx === -1) return
    state.taskLabels[idx] = { ...state.taskLabels[idx], name, color }
    await saveTaskLabels()
  }

  const deleteTaskLabel = async (labelId: string): Promise<void> => {
    state.taskLabels = state.taskLabels.filter((l) => l.id !== labelId)
    await saveTaskLabels()
  }

  const getLabelById = (labelId: string): TaskLabel | undefined => {
    return state.taskLabels.find((l) => l.id === labelId)
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    subscribeTasks,
    unsubscribeTasks,
    fetchTasks,
    fetchTaskById,
    createTask,
    updateTask,
    updateTaskStatus,
    deleteTask,
    toggleChecklistItem,
    addChecklistItem,
    removeChecklistItem,
    updateBoardOrder,
    archiveTask,
    unarchiveTask,
    updateTaskList,
    reorderTasksInList,
    fetchTaskLabels,
    createTaskLabel,
    updateTaskLabel,
    deleteTaskLabel,
    getLabelById,
  }
}
